import { FormattedMessage } from "react-intl";

import CopyToClipboard from "components/molecules/CopyToClipboard/CopyToClipboard";
import { DateTimeFormat, formatDate, parseDate } from "helpers/dateTimeHelper";

interface TopNavLocationDetailProps {
  type: "pickup" | "delivery";
  addressName: string;
  addressFields: (string | undefined)[];
  copyFields: (string | undefined)[];
  date?: string;
  time?: string;
  endDate?: string;
  endTime?: string;
  isAsap?: boolean;
  hasAddressV2: boolean;
  renderAddressV2: () => React.ReactNode;
  automationIdPrefix?: string;
}

export function renderDateRange(date: string, time: string, endDate?: string, endTime?: string) {
  if (endDate && endTime) {
    const startFormatted = formatDate(`${date} ${time}`, DateTimeFormat.fullDateTimeFormat);
    const endFormatted =
      endDate !== date
        ? formatDate(`${endDate} ${endTime}`, DateTimeFormat.fullDateTimeFormat)
        : formatDate(parseDate(endTime, "HH:mm:ss"), DateTimeFormat.timeFormat);
    return `${startFormatted} till ${endFormatted}`;
  }
  return formatDate(`${date} ${time}`, DateTimeFormat.fullDateTimeFormat);
}

// This component is used by both Quote Request Details and Quick Request Details in the TopNav.
function TopNavLocationDetail({
  type,
  addressName,
  addressFields,
  copyFields,
  date,
  time,
  endDate,
  endTime,
  isAsap,
  hasAddressV2,
  renderAddressV2,
  automationIdPrefix,
}: TopNavLocationDetailProps) {
  const idPrefix = automationIdPrefix || `topnav-v2-location-${type}`;
  const labelId = type === "pickup" ? "new-request.schedule.cargoPickup" : "new-request.schedule.cargoDelivery";
  const labelDefault = type === "pickup" ? "Pickup" : "Delivery";

  return (
    <div
      className="topnav-v2__detail-col"
      data-testid={`${idPrefix}-container`}
    >
      <span className="topnav-v2__detail-label" data-testid={`${idPrefix}-label`}>
        <FormattedMessage id={labelId} defaultMessage={labelDefault} />
      </span>
      {hasAddressV2 ? (
        <div className="topnav-v2__location-v2" data-testid={`${idPrefix}-address-v2`}>
          {renderAddressV2()}
        </div>
      ) : (
        <div style={{ position: "relative" }}>
          <CopyToClipboard copyOnly text={copyFields.filter(Boolean).join(", ")}>
            <div className="topnav-v2__location-name" data-testid={`${idPrefix}-name`}>
              {addressName}
            </div>
            <div className="topnav-v2__location-address" data-testid={`${idPrefix}-address`}>
              {addressFields.filter(Boolean).join(", ")}
            </div>
          </CopyToClipboard>
        </div>
      )}
      {isAsap && (
        <span className="topnav-v2__location-date" data-testid={`${idPrefix}-asap-value`}>
          <FormattedMessage id="new-request.schedule.defaultCargoReadyDeliveryOption.asap" defaultMessage="ASAP Delivery" />
        </span>
      )}
      {!isAsap && date && time && (
        <span className="topnav-v2__location-date" data-testid={`${idPrefix}-date-value`}>
          {renderDateRange(date, time, endDate, endTime)}
        </span>
      )}
    </div>
  );
}

export default TopNavLocationDetail;

TopNavLocationDetail.defaultProps = {
  date: undefined,
  time: undefined,
  endDate: undefined,
  endTime: undefined,
  isAsap: false,
  automationIdPrefix: undefined,
};
